import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "./authService.tsx";
import { toast } from "react-toastify";

interface AuthState {
  user: any | null;
  access: string | null;
  refresh: string | null;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  message: string | null;
}

const initialState: AuthState = {
  user: null,
  access: localStorage.getItem("accessToken") || null,
  refresh: localStorage.getItem("refreshToken") || null,
  isLoading: false,
  isError: false,
  isSuccess: false,
  message: null,
};

// Async Thunk for login
export const loginUser = createAsyncThunk(
  "auth/login",
  async (userData: { email: string; password: string }, thunkAPI) => {
    try {
      const response = await authService.login(userData);
      localStorage.setItem("accessToken", response.data.accessToken); // Store token immediately
      localStorage.setItem("refreshToken", response.data.refreshToken); // Store token immediately

      return response; // Return the API response
    } catch (error: any) {
      // Check if the status code is 400
      if (error?.response?.status === 400) {
        // Use the message field for status 400
        const message = error?.response?.data?.message || "Login failed";
        return thunkAPI.rejectWithValue(message);
      }

      // Use the existing logic for other errors
      const message = error?.response?.data?.errors?.password || "Login failed";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Auth Slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout_resetState: (state) => {
      state.user = null;
      state.access = null;
      state.refresh = null;
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
    },
    resetAuthState: (state) => {
      state.isLoading = false;
      state.isError = false;
      state.isSuccess = false;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        toast.success(action?.payload?.message);
        state.isLoading = false;
        state.isSuccess = true;
        state.user = action.payload.user;
        state.access = action?.payload?.data?.accessToken;
        state.refresh = action?.payload?.data?.refreshToken;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      });
  },
});

export const { logout_resetState, resetAuthState } = authSlice.actions;
export default authSlice.reducer;
